/**
 * Video — the «Видео» tab: the player on top, the clips of the channel under it.
 *
 * Tasks 13 and 14, `spec.md` stories 44–51 and «Видео»: the player on top, the clips under it —
 * YouTube and mp4 together, newest first. Task 75, stories 197–199: the list plays in a row, with
 * «Назад» and «Дальше» under the frame and the name of the clip that comes next. Task 76, stories
 * 200–201: a field over the list narrows it, and the row plays on through what it found. Task 77,
 * stories 202–204: a heart on every clip and on the key between the two, and «Все · Избранные».
 *
 * ```
 * Видео
 * ┌──────────────────────────────┐   ← the frame: YouTube's player, 16:9, stays on screen
 * │            ▶                 │     while the list scrolls under it — the frame alone
 * └──────────────────────────────┘
 * Koffee - Toast            Открыть в YouTube   ← always there, the frame working or not
 * YouTube · Koffee
 * [ |◀ Назад ] [ ♥ В избранном ] [ Дальше ▶| ]  ← tasks 75 and 77, the keys of the track card
 * Следующий: Spice - Go Down Deh
 * [ 🔍 Найти ролик: исполнитель, название ]      ← task 76, «Поиск»'s own field
 * [   Все · 150   |   Избранные · 12   ]         ← task 77, the switch of the sections
 * ── строка ролика ──────────────── ♡ ─          ← task 77, a track row's heart
 * ── строка ролика ──────────────── ♥ ─
 * ```
 *
 * ## What happens when
 *
 * - **Opening the tab** asks the server for the clips and, once there is one to show, puts
 *   its player into the area with the newest clip SHOWN, not started — a tab opened is not a
 *   clip tapped. For a link that is YouTube's player, and the moment YouTube's script is first
 *   asked for, never before (`engine/youtube.ts`); for an mp4 it is the one video element
 *   (`video/element.ts`), with Telegram's frame of the clip as its picture.
 * - **A tap on a clip** puts it into the frame and starts it. The music pauses when it really
 *   starts (`engine/video.ts`). On a phone that does not let a page start a frame's sound,
 *   YouTube's own play button in the frame starts it, and the music pauses then.
 * - **An mp4** plays in the video element, with the phone's own play button, seek bar and
 *   full-screen; the bar seeks over byte ranges of the server. The area holds one player at a
 *   time: a tap on an mp4 takes YouTube's frame out, a tap on a link takes the element out.
 * - **A clip ending** starts the next one down the list; **«Дальше»** does the same at once,
 *   **«Назад»** goes up (`engine/video.ts`, «Clips in a row»). «Назад» rests on the newest clip,
 *   «Дальше» on the oldest of the whole list; the end of the loaded pages is not an end — the list
 *   loads on. A start the phone refuses leaves the gold «играть» in the frame.
 * - **Typing in the field** narrows the list, from the second letter, to the clips the server
 *   finds — Latin and Cyrillic, as «Поиск» finds, and all of them — after a pause in typing
 *   (`store/video.ts`). «Дальше» and «Следующий: …» go through what was found; a clip playing on
 *   from before is followed by the first found, and has no «Назад». The clip in the frame is not
 *   touched. An emptied field gives the whole list back; nothing found is one line in place of
 *   the list.
 * - **Scrolling the list** keeps the frame alone at the top. The name, the keys, «Следующий: …»,
 *   the field and the switch go up with the list: kept up with the frame they held 525 of 932
 *   points of a phone, and in a Mac window left the list no row — the owner chose variant «А»,
 *   2026-10-26, over keeping the keys up too. A tap on the tab «Видео» brings them back
 *   (`app/Shell.tsx`: a tab tapped starts at its top).
 * - **A heart** — on a row, or the key «В избранном» for the clip in the frame — is the same heart
 *   a track has (`store/favorites.ts`): it answers the finger at once, the server follows.
 *   **«Избранные»** shows the clips with a heart, newest heart first, and they play in a row; a
 *   heart taken off takes its clip out of what plays at once, and its row at the next look — as in
 *   «Моё», no row moves from under the thumb. A section chosen starts at its top. «Моё» stays
 *   tracks only.
 * - **Leaving the tab** takes the player out of the page, and its sound with it.
 * - **Eight seconds without YouTube**, or «встраивание запрещено», put «YouTube не открывается
 *   без VPN» over the frame. «Открыть в YouTube» is under the frame in every state, and opens
 *   the clip through Telegram — in the YouTube app or the browser, with the Mini App left
 *   where it was.
 *
 * ## The bill's frame (task 72)
 *
 * Until a clip is started, a round gold «играть» stands in the middle of the frame — a tap on it
 * is a tap on that clip in the list (`video().play`), nothing more: where a phone refuses to start
 * a frame from the page, YouTube's own button is under it, as before. The name of the clip under
 * the frame is gold; the clips below are large pictures with their length on them.
 *
 * Nothing on this screen promises a clip that plays with the app minimised: YouTube does not
 * play without its frame on screen, and that is YouTube's rule, not ours (`spec.md` «Видео»).
 */

import type { JSX } from 'react';
import { useCallback, useEffect, useRef } from 'react';

import { Cover } from '../components/Cover';
import { FavoriteButton } from '../components/FavoriteButton';
import { NextIcon, PreviousIcon } from '../components/PlayerControls';
import { ScreenHead } from '../components/ScreenHead';
import { SearchBox } from '../components/SearchBox';
import { ListState } from '../components/TrackListView';
import { VideoRow, videoByline } from '../components/VideoRow';
import { video } from '../engine/video';
import type { VideoItem } from '../lib/api';
import { clipCount } from '../lib/format';
import { telegram } from '../lib/telegram';
import { useFavorites } from '../store/favorites';
import { aroundIn, shownClip, usePlaylist, useVideo } from '../store/video';
import type { VideoView } from '../store/video';
import '../styles/tracks.css';
import '../styles/video.css';

/* ────────────────────────────────────────────────────────────────────────────
 * Copy
 * ──────────────────────────────────────────────────────────────────────────── */

export const VIDEO_TITLE = 'Видео';
export const VIDEO_LOADING_TEXT = 'Загружаю…';
export const EMPTY_VIDEO_TEXT =
  'Здесь появятся клипы канала: ролики YouTube и видео, выложенные файлом.';
/** `spec.md` «Видео», word for word. */
export const NO_VPN_TEXT = 'YouTube не открывается без VPN';
export const CLIP_FAILED_TEXT = 'Этот ролик не запускается здесь. Его можно открыть на YouTube.';
/**
 * An mp4 the phone could not play: the network dropped it, or the phone lacks its codec —
 * `spec.md` «Что проверяется только руками»: «кодек — решение конкретного телефона». Nothing to
 * open elsewhere, so no way out is offered but a tap on its row, which tries again.
 */
export const MP4_FAILED_TEXT =
  'Это видео не запускается на этом телефоне. Нажмите на него в списке ещё раз.';
export const OPEN_IN_YOUTUBE_LABEL = 'Открыть в YouTube';
export const MORE_VIDEOS_LABEL = 'Показать ещё';
export const PLAYER_LABEL = 'Плеер';
export const RETRY_LABEL = 'Ещё раз';
/** Task 72: the gold button in the frame before a clip is started. */
export const START_CLIP_LABEL = 'Включить ролик';
/** Task 75: the keys under the frame, and the line under them. */
export const PREVIOUS_CLIP_LABEL = 'Назад';
export const NEXT_CLIP_LABEL = 'Дальше';
export const COMING_NEXT_TEXT = 'Следующий:';
/** Task 76: the field over the list; the placeholder is the approved picture's. */
export const CLIP_SEARCH_LABEL = 'Найти ролик';
export const CLIP_SEARCH_PLACEHOLDER = 'Найти ролик: исполнитель, название';
export const CLIP_SEARCH_CLEAR_LABEL = 'Очистить поиск роликов';
/** The field's request failed with nothing to say why. The list under it stays as it was. */
export const CLIP_SEARCH_FAILED_TEXT = 'Поиск роликов не удался.';

/** Task 77: the words of the heart key under the frame, as on the approved picture. */
export const FAVORITE_CLIP_WORDS = Object.freeze({ off: 'В избранное', on: 'В избранном' });
/** Task 77: the switch over the list. */
export const ALL_CLIPS_LABEL = 'Все';
export const FAVORITE_CLIPS_LABEL = 'Избранные';
export const VIEW_SWITCH_LABEL = 'Какие ролики';
export const NO_FAVORITE_CLIPS_TEXT = 'Здесь будут ролики с ♥: нажмите ♥ у ролика в «Все».';

/** Task 76: the one line in place of the list when the field found nothing — no error. */
export function noClipsFound(text: string): string {
  return `По запросу «${text.trim()}» роликов не нашлось.`;
}

/** Task 77: the same in «Избранные» — the field may have found clips, none of them with a heart. */
export function noFavoriteClipsFound(text: string): string {
  return `По запросу «${text.trim()}» среди избранных роликов нет.`;
}

/**
 * Task 77: « · 150» after a section's word, as on the approved picture. For the eye only, like the
 * numbers of «Моё» (task 72): a section is named by its word, a list's length is said by its rows.
 */
export function viewCount(count: number): string {
  return ` · ${String(count)}`;
}

/** A tap on a clip of the list: into the frame, and started. */
function playClip(item: VideoItem): void {
  video().play(item);
}

/** Task 77: a heart put on a clip here heads «Избранные» at once, before the server answers. */
function noteHeart(item: VideoItem, on: boolean): void {
  if (on) useVideo.getState().noteFavoriteClip(item);
}

/* ────────────────────────────────────────────────────────────────────────────
 * Pieces
 * ──────────────────────────────────────────────────────────────────────────── */

/**
 * «Открыть в YouTube»: a real link, so that it works in a plain browser too, handed to
 * Telegram when there is a Telegram to hand it to — which then opens it outside the Mini App.
 */
function OpenInYoutube({ url }: { readonly url: string }): JSX.Element {
  return (
    <a
      className="linkButton videoOpen"
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      onClick={(event) => {
        if (telegram.openLink(url)) event.preventDefault();
      }}
    >
      {OPEN_IN_YOUTUBE_LABEL}
    </a>
  );
}

/** The player area: the frame, its picture until it answers, and what went wrong if anything. */
function Stage({ shown }: { readonly shown: VideoItem }): JSX.Element {
  const host = useRef<HTMLDivElement | null>(null);
  const frame = useVideo((s) => s.frame);
  const problem = useVideo((s) => s.problem);
  const playback = useVideo((s) => s.playback);

  // Once, while the tab is on screen: the player of the clip there is to show goes in, and
  // comes out when the tab goes. Which clip is read from the slice at that moment, so a clip
  // opened from search is the one the area starts with; every later tap goes to the area that
  // is already there, and swaps the player when the kind changes (`engine/video.ts`, `play`).
  useEffect(() => {
    const element = host.current;
    const first = shownClip(useVideo.getState());
    if (element === null || first === undefined) return;
    video().attach(element, first);
    return () => {
      video().detach();
    };
  }, []);

  const notice =
    problem === 'failed'
      ? shown.kind === 'video'
        ? MP4_FAILED_TEXT
        : CLIP_FAILED_TEXT
      : frame === 'unavailable' || problem === 'refused'
        ? NO_VPN_TEXT
        : undefined;

  return (
    <section className="videoStage" aria-label={PLAYER_LABEL}>
      {frame !== 'ready' && <Cover src={shown.cover} className="videoPoster" />}
      <div ref={host} className="videoFrameHost" />
      {notice === undefined && playback === 'idle' && (
        <button
          type="button"
          className="videoStart"
          aria-label={START_CLIP_LABEL}
          onClick={() => {
            video().play(shown);
          }}
        >
          <svg viewBox="0 0 24 24" aria-hidden="true" className="videoStartIcon">
            <path d="M8 5v14l11-7z" fill="currentColor" />
          </svg>
        </button>
      )}
      {notice !== undefined && (
        <div className="videoNotice" role="status">
          <p className="videoNoticeText">{notice}</p>
        </div>
      )}
    </section>
  );
}

/**
 * Task 75: «Назад» and «Дальше» — the keys of the track card — and what comes next. «Дальше»
 * rests only at the end of the whole list: past the loaded pages it loads on. The line under the
 * keys keeps its height without a name, so the list does not jump when the last clip comes.
 * Task 77: between them the heart of the clip in the frame, with its words.
 */
function ClipKeys({ shown }: { readonly shown: VideoItem }): JSX.Element {
  const listed = usePlaylist();
  const { previous, next } = aroundIn(listed, shown);
  const hearted = useCallback(
    (on: boolean): void => {
      noteHeart(shown, on);
    },
    [shown],
  );

  return (
    <>
      <div className="cardKeys">
        <button
          type="button"
          className="key cardKey"
          disabled={previous === undefined}
          onClick={() => {
            video().previous();
          }}
        >
          <PreviousIcon className="keyIcon" />
          <span>{PREVIOUS_CLIP_LABEL}</span>
        </button>
        <FavoriteButton
          trackId={shown.id}
          title={shown.title}
          className="key cardKey"
          iconClassName="keyIcon"
          words={FAVORITE_CLIP_WORDS}
          onToggle={hearted}
        />
        <button
          type="button"
          className="key cardKey"
          disabled={next === undefined && listed.complete}
          onClick={() => {
            video().next();
          }}
        >
          <NextIcon className="keyIcon" />
          <span>{NEXT_CLIP_LABEL}</span>
        </button>
      </div>
      <p className="videoComing">
        {next !== undefined && (
          <>
            <span className="videoComingLabel">{COMING_NEXT_TEXT}</span> {next.title}
          </>
        )}
      </p>
    </>
  );
}

/**
 * Task 77: «Все · Избранные» — the switch of the sections «Треки» and «Моё» have, with how many
 * in each. «Все» counts the channel's clips, «Избранные» the clips whose heart is on now. A word
 * and its number are one run of text: the button lays its children out in a row, and a row would
 * drop the space before «·». It stands right over the list, so a section chosen starts under it.
 */
function ViewSwitch(): JSX.Element {
  const view = useVideo((s) => s.view);
  const total = useVideo((s) => s.total);
  const favoriteClips = useVideo((s) => s.favoriteClips);
  // Only how many: a heart that does not change the number redraws nothing here.
  const hearted = useFavorites((s) => favoriteClips?.filter((clip) => s.ids.has(clip.id)).length);
  const sections: readonly { id: VideoView; label: string; count: number | undefined }[] = [
    { id: 'all', label: ALL_CLIPS_LABEL, count: total },
    { id: 'favorites', label: FAVORITE_CLIPS_LABEL, count: hearted },
  ];
  return (
    <div className="segments" role="group" aria-label={VIEW_SWITCH_LABEL}>
      {sections.map((section) => (
        <button
          key={section.id}
          type="button"
          className="segment"
          aria-pressed={section.id === view}
          onClick={() => {
            useVideo.getState().setView(section.id);
          }}
        >
          <span>
            {section.label}
            {section.count !== undefined && (
              <span aria-hidden="true">{viewCount(section.count)}</span>
            )}
          </span>
        </button>
      ))}
    </div>
  );
}

/** Task 76: «Поиск»'s field over the list. No focus of its own: the tab is opened to watch. */
function ClipSearch(): JSX.Element {
  const text = useVideo((s) => s.searchText);
  const setText = useVideo((s) => s.setSearchText);
  return (
    <SearchBox
      value={text}
      onChange={setText}
      label={CLIP_SEARCH_LABEL}
      placeholder={CLIP_SEARCH_PLACEHOLDER}
      clearLabel={CLIP_SEARCH_CLEAR_LABEL}
    />
  );
}

/* ────────────────────────────────────────────────────────────────────────────
 * The screen
 * ──────────────────────────────────────────────────────────────────────────── */

export function VideoScreen(): JSX.Element {
  const items = useVideo((s) => s.items);
  const status = useVideo((s) => s.status);
  const failure = useVideo((s) => s.failure);
  const selected = useVideo((s) => s.selected);
  const loadingMore = useVideo((s) => s.loadingMore);
  const total = useVideo((s) => s.total);
  const search = useVideo((s) => s.search);
  const searchStatus = useVideo((s) => s.searchStatus);
  const searchFailure = useVideo((s) => s.searchFailure);
  const view = useVideo((s) => s.view);
  const favoriteClips = useVideo((s) => s.favoriteClips);
  const favoriteClipsFailure = useVideo((s) => s.favoriteClipsFailure);
  const listed = usePlaylist();

  // Every entry asks afresh: a clip posted a minute ago is there the next time the tab opens, and
  // a heart put on another phone is in «Избранные» — and drawn: the hearts are read again too.
  useEffect(() => {
    void useVideo.getState().load();
    void useVideo.getState().loadFavoriteClips();
    void useFavorites.getState().load();
  }, []);

  const shown = shownClip({ items, selected });
  const url = shown?.youtube?.url;
  const byline = shown === undefined ? '' : videoByline(shown);

  let list: JSX.Element;
  if (items.length === 0) {
    list =
      status === 'failed' ? (
        <ListState
          text={failure?.message ?? EMPTY_VIDEO_TEXT}
          onRetry={() => {
            void useVideo.getState().load();
          }}
        />
      ) : (
        <p className="empty">{status === 'ready' ? EMPTY_VIDEO_TEXT : VIDEO_LOADING_TEXT}</p>
      );
  } else if (view === 'favorites' && favoriteClips === undefined) {
    list =
      favoriteClipsFailure === undefined ? (
        <p className="empty">{VIDEO_LOADING_TEXT}</p>
      ) : (
        <ListState
          text={favoriteClipsFailure.message}
          onRetry={() => {
            void useVideo.getState().loadFavoriteClips();
          }}
        />
      );
  } else if (listed.items.length === 0) {
    // Narrowed to nothing — the whole list is never empty here: the field found no clip, or none
    // among «Избранные», or no clip has a heart.
    const text =
      search === undefined
        ? NO_FAVORITE_CLIPS_TEXT
        : view === 'favorites'
          ? noFavoriteClipsFound(search.text)
          : noClipsFound(search.text);
    list = <p className="empty">{text}</p>;
  } else {
    list = (
      <>
        {/* The last list stays while the field's next answer comes; it only fades. */}
        <ul className="videoList" aria-busy={searchStatus === 'loading'}>
          {listed.items.map((item) => (
            <VideoRow
              key={item.id}
              item={item}
              current={item.id === shown?.id}
              onPlay={playClip}
              onHeart={noteHeart}
            />
          ))}
        </ul>
        {!listed.complete && (
          <div className="videoMore">
            <button
              type="button"
              className="linkButton"
              disabled={loadingMore}
              onClick={() => {
                void useVideo.getState().loadMore();
              }}
            >
              {MORE_VIDEOS_LABEL}
            </button>
          </div>
        )}
      </>
    );
  }

  return (
    <div className="video">
      <header className="videoHead">
        <ScreenHead
          title={VIDEO_TITLE}
          count={total === undefined ? undefined : clipCount(total)}
        />
      </header>
      {shown !== undefined && (
        <>
          <div className="videoStick">
            <Stage shown={shown} />
          </div>
          <div className="videoTop">
            <div className="videoMeta">
              <span className="trackText">
                <span className="trackTitle">{shown.title}</span>
                {byline !== '' && <span className="trackByline">{byline}</span>}
              </span>
              {url !== undefined && <OpenInYoutube url={url} />}
            </div>
            <ClipKeys shown={shown} />
            <ClipSearch />
            <ViewSwitch />
          </div>
        </>
      )}
      {searchStatus === 'failed' && (
        <div className="videoSearchFailed" role="status">
          <p className="stateText">{searchFailure?.message ?? CLIP_SEARCH_FAILED_TEXT}</p>
          <button
            type="button"
            className="linkButton"
            onClick={() => {
              useVideo.getState().retrySearch();
            }}
          >
            {RETRY_LABEL}
          </button>
        </div>
      )}
      {list}
    </div>
  );
}
